import React, { useEffect, useState } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { FileUpload } from "@/components/FileUpload";
import { MultiImageUpload } from "@/components/MultiImageUpload";
import { SignaturePad } from "@/components/SignaturePad";
import { toast } from "sonner";
import {
  Loader2, Plus, Pencil, Trash2, X, Car, Clock, CheckCircle2, ShieldCheck,
  Camera, FileCheck2, KeyRound, AlertTriangle, Users, Fuel, Truck, IdCard,
} from "lucide-react";

const CAR_TYPES = ["Economy", "Sedan", "SUV", "Luxury", "Convertible", "Van", "Jeep 4x4", "Pickup"];
const FUEL = ["Full-to-Full", "Full-to-Empty", "Same-to-Same", "Pre-Purchase"];
const ISLANDS = ["Grenada", "St. Vincent and the Grenadines", "Barbados", "Saint Lucia", "Turks & Caicos", "Bahamas", "Jamaica"];
const inputCls = "w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50";

const EMPTY_CAR = {
  name: "", make: "", model: "", year: 2024, car_type: "SUV", passenger_capacity: 5,
  transmission: "Automatic", price_per_day: 75, original_price_per_day: 0, currency: "USD", fuel_policy: "Full-to-Full",
  security_deposit: 250, delivery_available: false, delivery_fee: 0, island: "Grenada",
  location: "", description: "", images: [""],
};

export default function CarProviderPanel() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/cars/provider/me/profile")
      .then((r) => setProfile(r.data))
      .catch(() => setProfile({ status: "none" }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-400" /></div>;

  if (profile.status !== "approved") {
    return <Onboarding profile={profile} onDone={load} />;
  }
  return <ApprovedPanel />;
}

// ---------------- Onboarding ----------------
function Onboarding({ profile, onDone }) {
  const pending = profile.status === "pending";
  const rejected = profile.status === "rejected";
  const [form, setForm] = useState({
    business_name: profile.business_name || "", phone: profile.phone || "",
    business_license_url: profile.business_license_url || "",
    fleet_insurance_url: profile.fleet_insurance_url || "",
    rental_agreement_url: profile.rental_agreement_url || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_license_url || !form.fleet_insurance_url || !form.rental_agreement_url) {
      toast.error("Please upload all three required documents"); return;
    }
    setSaving(true);
    try {
      await api.post("/cars/provider/apply", form);
      toast.success("Application submitted for review");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (pending) {
    return (
      <div className="glass rounded-2xl p-8 text-center max-w-lg mx-auto" data-testid="car-provider-pending">
        <div className="w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-4">
          <Clock className="w-7 h-7 text-amber-400" />
        </div>
        <h3 className="font-heading text-xl font-bold text-white">Application under review</h3>
        <p className="text-slate-400 text-sm mt-2">Your car rental documents are being verified by our team. You'll be notified once approved, then you can list vehicles.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 max-w-2xl space-y-5" data-testid="car-provider-onboarding">
      <div>
        <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-cyan-400" /> Become a Car Rental Provider</h3>
        <p className="text-slate-400 text-sm mt-1">Verify your business to list vehicles. All documents are reviewed by our team.</p>
      </div>
      {rejected && (
        <div className="flex items-center gap-2 text-sm text-[#FF5E7E] bg-[#FF5E7E]/10 rounded-xl px-3 py-2.5">
          <AlertTriangle className="w-4 h-4" /> Your previous application was not approved. Please review your documents and resubmit.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Business name</span>
          <input required data-testid="car-biz-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} placeholder="Island Wheels Ltd" /></label>
        <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Phone</span>
          <input data-testid="car-biz-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="+1 473 555 1234" /></label>
      </div>
      <FileUpload label="Business License" testid="upload-business-license" value={form.business_license_url} onUploaded={(u) => set("business_license_url", u)} />
      <FileUpload label="Fleet Insurance" testid="upload-fleet-insurance" value={form.fleet_insurance_url} onUploaded={(u) => set("fleet_insurance_url", u)} />
      <FileUpload label="Rental Agreement template" testid="upload-rental-agreement" value={form.rental_agreement_url} onUploaded={(u) => set("rental_agreement_url", u)} />
      <button disabled={saving} data-testid="car-provider-submit" className="w-full btn-neon rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck2 className="w-4 h-4" />} Submit for verification
      </button>
    </form>
  );
}

// ---------------- Approved panel ----------------
function ApprovedPanel() {
  const [tab, setTab] = useState("fleet");
  return (
    <div>
      <div className="flex gap-2 mb-6">
        {[
          { key: "fleet", label: "My Fleet", icon: Car },
          { key: "bookings", label: "Rentals", icon: KeyRound },
        ].map((t) => (
          <button key={t.key} data-testid={`car-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "btn-neon" : "glass text-slate-300 hover:text-cyan-400"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "fleet" ? <Fleet /> : <Rentals />}
    </div>
  );
}

function Fleet() {
  const [cars, setCars] = useState(null);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/cars/provider/me").then((r) => setCars(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => setModal({ mode: "create", data: { ...EMPTY_CAR } });
  const openEdit = (c) => setModal({ mode: "edit", id: c.id, data: { ...EMPTY_CAR, ...c, images: c.images?.length ? c.images : [c.image_url || ""] } });
  const setField = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const d = modal.data;
    const payload = {
      name: d.name, make: d.make, model: d.model, year: Number(d.year) || null,
      car_type: d.car_type, passenger_capacity: Number(d.passenger_capacity),
      transmission: d.transmission, price_per_day: Number(d.price_per_day), original_price_per_day: Number(d.original_price_per_day) || 0, currency: d.currency || "USD",
      fuel_policy: d.fuel_policy, security_deposit: Number(d.security_deposit),
      delivery_available: !!d.delivery_available, delivery_fee: Number(d.delivery_fee) || 0,
      island: d.island, location: d.location, description: d.description,
      images: (d.images || []).filter(Boolean),
    };
    try {
      if (modal.mode === "create") await api.post("/cars", payload);
      else await api.put(`/cars/${modal.id}`, payload);
      toast.success(modal.mode === "create" ? "Vehicle submitted for approval" : "Vehicle updated (re-submitted for approval)");
      setModal(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/cars/${id}`); toast.success("Vehicle removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (cars === null) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-400" /></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={openNew} data-testid="new-car-btn" className="btn-neon rounded-full px-5 py-2.5 font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add vehicle</button>
      </div>
      {cars.length === 0 ? (
        <div className="text-center py-16 glass rounded-2xl">
          <Car className="w-9 h-9 text-slate-600 mx-auto mb-3" />
          <p className="text-white font-heading text-lg">No vehicles yet</p>
          <p className="text-slate-400 text-sm mt-1">Add your first vehicle — each listing is reviewed before going live.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="car-fleet-list">
          {cars.map((c) => (
            <div key={c.id} data-testid={`fleet-car-${c.id}`} className="glass rounded-2xl p-4 flex flex-col sm:flex-row gap-4 items-start">
              <img src={c.image_url ? fileUrl(c.image_url) : "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=85"} alt="" className="w-full sm:w-32 h-24 rounded-xl object-cover" />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-semibold text-white">{c.name}</h3>
                  <StatusPill status={c.status} />
                </div>
                <p className="text-xs text-slate-400 mt-1">{c.car_type} · {c.passenger_capacity} seats · {c.island}</p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="font-heading font-bold text-white">${c.price_per_day}<span className="text-xs text-slate-500 font-normal"> /day</span></span>
                  <span className="text-xs text-slate-400 flex items-center gap-1"><Fuel className="w-3.5 h-3.5" /> {c.fuel_policy}</span>
                  {c.delivery_available && <span className="text-xs text-emerald-400 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> ${c.delivery_fee} delivery</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(c)} data-testid={`edit-car-${c.id}`} className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-cyan-400"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(c.id)} data-testid={`delete-car-${c.id}`} className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-[#FF5E7E]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setModal(null)}>
          <div className="glass neon-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="car-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-bold text-white">{modal.mode === "create" ? "Add vehicle" : "Edit vehicle"}</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Vehicle name</span><input required data-testid="car-name-input" value={modal.data.name} onChange={(e) => setField("name", e.target.value)} className={inputCls} placeholder="Suzuki Jimny 4x4" /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Make</span><input value={modal.data.make} onChange={(e) => setField("make", e.target.value)} className={inputCls} placeholder="Suzuki" /></label>
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Model</span><input value={modal.data.model} onChange={(e) => setField("model", e.target.value)} className={inputCls} placeholder="Jimny" /></label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Type</span>
                  <select data-testid="car-type-input" value={modal.data.car_type} onChange={(e) => setField("car_type", e.target.value)} className={inputCls}>{CAR_TYPES.map((t) => <option key={t} value={t} className="bg-[#0B0E1B]">{t}</option>)}</select></label>
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Seats</span><input type="number" min={1} data-testid="car-capacity-input" value={modal.data.passenger_capacity} onChange={(e) => setField("passenger_capacity", e.target.value)} className={inputCls} /></label>
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Year</span><input type="number" value={modal.data.year || ""} onChange={(e) => setField("year", e.target.value)} className={inputCls} /></label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Price / day ($)</span><input type="number" min={1} required data-testid="car-price-input" value={modal.data.price_per_day} onChange={(e) => setField("price_per_day", e.target.value)} className={inputCls} /></label>
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Was / day ($) <span className="text-slate-600">— deal</span></span><input type="number" min={0} data-testid="car-original-price-input" value={modal.data.original_price_per_day} onChange={(e) => setField("original_price_per_day", e.target.value)} className={inputCls} placeholder="0 = no deal" /></label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Security deposit ($)</span><input type="number" min={0} data-testid="car-deposit-input" value={modal.data.security_deposit} onChange={(e) => setField("security_deposit", e.target.value)} className={inputCls} /></label>
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Seats</span><input type="number" min={1} value={modal.data.passenger_capacity} onChange={(e) => setField("passenger_capacity", e.target.value)} className={inputCls} /></label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Transmission</span>
                  <select value={modal.data.transmission} onChange={(e) => setField("transmission", e.target.value)} className={inputCls}><option className="bg-[#0B0E1B]">Automatic</option><option className="bg-[#0B0E1B]">Manual</option></select></label>
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Fuel policy</span>
                  <select data-testid="car-fuel-input" value={modal.data.fuel_policy} onChange={(e) => setField("fuel_policy", e.target.value)} className={inputCls}>{FUEL.map((f) => <option key={f} value={f} className="bg-[#0B0E1B]">{f}</option>)}</select></label>
              </div>
              <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Island</span>
                <select value={modal.data.island} onChange={(e) => setField("island", e.target.value)} className={inputCls}>{ISLANDS.map((i) => <option key={i} value={i} className="bg-[#0B0E1B]">{i}</option>)}</select></label>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" data-testid="car-delivery-toggle" checked={modal.data.delivery_available} onChange={(e) => setField("delivery_available", e.target.checked)} className="accent-cyan-400 w-4 h-4" />
                Offer airport / hotel delivery
              </label>
              {modal.data.delivery_available && (
                <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Delivery fee ($)</span><input type="number" min={0} data-testid="car-delivery-fee-input" value={modal.data.delivery_fee} onChange={(e) => setField("delivery_fee", e.target.value)} className={inputCls} /></label>
              )}
              <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Description</span><textarea rows={3} value={modal.data.description} onChange={(e) => setField("description", e.target.value)} className={inputCls} placeholder="Compact, fuel-efficient 4x4…" /></label>
              <div><span className="text-xs text-slate-400 mb-1.5 block">Vehicle photos</span><MultiImageUpload testid="car-photos-upload" value={modal.data.images} onChange={(imgs) => setField("images", imgs)} max={6} /></div>
              <button disabled={saving} data-testid="save-car-btn" className="w-full btn-neon rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : modal.mode === "create" ? "Submit for approval" : "Save changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Rentals() {
  const [bookings, setBookings] = useState(null);
  const [inspect, setInspect] = useState(null); // {booking, type}
  const [complete, setComplete] = useState(null); // booking

  const load = () => api.get("/cars/provider/me/bookings").then((r) => setBookings(r.data));
  useEffect(() => { load(); }, []);

  if (bookings === null) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-400" /></div>;
  if (bookings.length === 0) return (
    <div className="text-center py-16 glass rounded-2xl"><KeyRound className="w-9 h-9 text-slate-600 mx-auto mb-3" /><p className="text-white font-heading text-lg">No rentals yet</p></div>
  );

  return (
    <div className="space-y-3" data-testid="car-rentals-list">
      {bookings.map((b) => (
        <div key={b.id} data-testid={`rental-${b.id}`} className="glass rounded-2xl p-4">
          <div className="flex items-start gap-4">
            <img src={b.car_image ? fileUrl(b.car_image) : "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=85"} alt="" className="w-24 h-16 rounded-lg object-cover" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-semibold text-white">{b.car_name}</h3>
                <StatusPill status={b.status} />
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${b.deposit?.status === "released" ? "text-emerald-400 border-emerald-400/30" : b.deposit?.status === "captured" ? "text-[#FF5E7E] border-[#FF5E7E]/30" : "text-amber-400 border-amber-400/30"}`}>
                  deposit: {b.deposit?.status}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1"><Users className="w-3.5 h-3.5 inline mr-1" />{b.traveler_name} · {b.pickup_date} → {b.dropoff_date}</p>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {b.delivery_option === "delivery" && <span className="text-xs text-emerald-400 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {b.delivery_address || `Flight ${b.flight_number}`}</span>}
                {b.drivers_license_url && <a href={fileUrl(b.drivers_license_url)} target="_blank" rel="noreferrer" className="text-xs text-cyan-300 hover:underline flex items-center gap-1"><IdCard className="w-3.5 h-3.5" /> License</a>}
                <span className="text-xs text-white font-semibold">${b.total}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            {b.status === "confirmed" && (
              <button data-testid={`pickup-inspect-${b.id}`} onClick={() => setInspect({ booking: b, type: "pickup" })} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25">
                <Camera className="w-4 h-4" /> Pickup inspection
              </button>
            )}
            {b.status === "picked_up" && (
              <button data-testid={`return-inspect-${b.id}`} onClick={() => setInspect({ booking: b, type: "return" })} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-fuchsia-500/15 text-fuchsia-300 hover:bg-fuchsia-500/25">
                <Camera className="w-4 h-4" /> Return inspection
              </button>
            )}
            {b.status === "returned" && (
              <button data-testid={`complete-rental-${b.id}`} onClick={() => setComplete(b)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25">
                <CheckCircle2 className="w-4 h-4" /> Rental completed
              </button>
            )}
            {b.status === "completed" && <span className="text-sm text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Completed · deposit released</span>}
          </div>
        </div>
      ))}

      {inspect && <InspectionModal booking={inspect.booking} type={inspect.type} onClose={() => setInspect(null)} onDone={() => { setInspect(null); load(); }} />}
      {complete && <CompleteModal booking={complete} onClose={() => setComplete(null)} onDone={() => { setComplete(null); load(); }} />}
    </div>
  );
}

function InspectionModal({ booking, type, onClose, onDone }) {
  const [photos, setPhotos] = useState({ front: "", back: "", left: "", right: "" });
  const [sig, setSig] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const setPhoto = (k, v) => setPhotos((p) => ({ ...p, [k]: v }));
  const allPhotos = photos.front && photos.back && photos.left && photos.right;

  const submit = async () => {
    if (!allPhotos) { toast.error("Upload all 4 vehicle photos"); return; }
    if (!sig) { toast.error("Capture the guest's signature"); return; }
    setSaving(true);
    try {
      await api.post(`/car-bookings/${booking.id}/inspection`, {
        type, photo_front: photos.front, photo_back: photos.back,
        photo_left: photos.left, photo_right: photos.right, signature_url: sig, notes,
      });
      toast.success(`${type === "pickup" ? "Pickup" : "Return"} inspection recorded`);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="inspection-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2"><Camera className="w-5 h-5 text-cyan-400" /> {type === "pickup" ? "Pickup" : "Return"} inspection</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-5">Photograph all four sides of the vehicle, then have the guest sign to confirm the condition.</p>
        <div className="grid grid-cols-2 gap-3">
          {[["front", "Front"], ["back", "Back"], ["left", "Left"], ["right", "Right"]].map(([k, label]) => (
            <div key={k}>
              <span className="text-xs text-slate-400 mb-1.5 block">{label}</span>
              <FileUpload testid={`inspect-photo-${k}`} accept="image/*" value={photos[k]} onUploaded={(u) => setPhoto(k, u)} compact />
            </div>
          ))}
        </div>
        <label className="block mt-4"><span className="text-xs text-slate-400 mb-1.5 block">Condition notes (optional)</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder="Minor scratch on rear bumper…" /></label>
        <div className="mt-4">
          <span className="text-xs text-slate-400 mb-1.5 block">Guest signature</span>
          <SignaturePad testid="inspection-signature" value={sig} onSigned={setSig} />
        </div>
        <button onClick={submit} disabled={saving} data-testid="inspection-submit" className="w-full btn-neon rounded-xl py-3 font-semibold flex items-center justify-center gap-2 mt-5 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck2 className="w-4 h-4" />} Confirm inspection
        </button>
      </div>
    </div>
  );
}

function CompleteModal({ booking, onClose, onDone }) {
  const [noDamage, setNoDamage] = useState(true);
  const [notes, setNotes] = useState("");
  const [amount, setAmount] = useState(0);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/car-bookings/${booking.id}/complete`, {
        no_damage: noDamage, damage_notes: notes, damage_amount: Number(amount) || 0,
      });
      toast.success(noDamage ? "Rental completed · deposit released" : "Rental completed · deposit captured");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="complete-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-400" /> Complete rental</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-5">Confirm the vehicle's condition to release or capture the ${booking.deposit?.amount} security deposit hold.</p>
        <div className="space-y-2">
          <button type="button" data-testid="complete-nodamage" onClick={() => setNoDamage(true)} className={`w-full text-left rounded-xl p-4 border transition-all ${noDamage ? "neon-border bg-[#0B0E1B]" : "border-[#1D2640] glass"}`}>
            <p className="font-semibold text-white text-sm">No damage — release deposit</p>
            <p className="text-xs text-slate-400">The full hold is released back to the guest.</p>
          </button>
          <button type="button" data-testid="complete-damage" onClick={() => setNoDamage(false)} className={`w-full text-left rounded-xl p-4 border transition-all ${!noDamage ? "border-[#FF5E7E]/50 bg-[#0B0E1B]" : "border-[#1D2640] glass"}`}>
            <p className="font-semibold text-white text-sm">Damage reported — capture deposit</p>
            <p className="text-xs text-slate-400">Capture part or all of the hold for repairs.</p>
          </button>
        </div>
        {!noDamage && (
          <div className="mt-3 space-y-2">
            <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Amount to capture ($)</span><input type="number" min={0} max={booking.deposit?.amount} value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} /></label>
            <label className="block"><span className="text-xs text-slate-400 mb-1.5 block">Damage notes</span><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} /></label>
          </div>
        )}
        <button onClick={submit} disabled={saving} data-testid="complete-submit" className="w-full btn-neon rounded-xl py-3 font-semibold flex items-center justify-center gap-2 mt-5 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Complete rental"}
        </button>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  pending: "text-amber-400 border-amber-400/30",
  approved: "text-emerald-400 border-emerald-400/30",
  rejected: "text-[#FF5E7E] border-[#FF5E7E]/30",
  confirmed: "text-cyan-300 border-cyan-300/30",
  picked_up: "text-fuchsia-400 border-fuchsia-400/30",
  returned: "text-orange-400 border-orange-400/30",
  completed: "text-emerald-400 border-emerald-400/30",
  cancelled: "text-slate-500 border-slate-600",
};
function StatusPill({ status }) {
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[status] || "text-slate-400 border-slate-600"}`}>{(status || "").replace("_", " ")}</span>;
}
